import Styles from './styles.module.css';
import React, { useEffect, useState } from 'react';
import { addDoc, arrayRemove, arrayUnion, collection, deleteDoc, doc, getFirestore, updateDoc } from 'firebase/firestore';
import { RxCross2 } from "react-icons/rx";

// 卒業生(graduation)の1件。サロンが登録したもの(companyId)と、学校が追加したもの
// (addedBySchoolId・schoolCompanyId)がある。
export type GraduationDoc = {
  id: string;
  companyId?: string;
  addedBySchoolId?: string;
  schoolCompanyId?: string;
  school?: string;
  name?: string;
  graduation_years?: string;
  postion?: string;
};

// その卒業生がどのサロンの卒業生か(この学校から見て)。他校が追加したものは ''(数えない・出さない)。
export const graduationCompanyId = (g: GraduationDoc, schoolId: string): string => {
  if (g.addedBySchoolId) return g.addedBySchoolId === schoolId ? (g.schoolCompanyId ?? '') : '';
  return g.companyId ?? '';
};

type SchoolGraduationProps = {
  schoolName: string;
  schoolId: string;
  toggle: { type: string; id: string; name: string };
  setToggle: React.Dispatch<React.SetStateAction<any>>;
  graduationDocs: GraduationDoc[];
  hiddenGraduationIds: string[];
};

// 求人一覧の「卒業生」から開く、サロンごとの卒業生一覧。
// サロンが登録した卒業生に加えて、学校が独自に卒業生を追加・削除できる。
//   - 学校が追加した卒業生: graduation に保存する。サロン側の画面に出さないよう companyId は持たせず、
//     対象のサロンは schoolCompanyId に入れる(サロンの一覧・求人スコアは companyId で探すため出ない)。
//   - サロンが登録した卒業生の「削除」(退社した人など): サロンのデータは消さず、この学校の画面でだけ
//     見えなくする(schools/{schoolId}.hiddenGraduationIds)。削除したものは「削除した卒業生」から戻せる。
const SchoolGraduation: React.FC<SchoolGraduationProps> = ({ toggle, setToggle, schoolName, schoolId, graduationDocs, hiddenGraduationIds }) => {

  const db = getFirestore();
  const close = () => setToggle({ type: "", id: "", name: "" });

  const [ name, setName ] = useState('');
  const [ years, setYears ] = useState('');
  const [ position, setPosition ] = useState('');
  const [ saving, setSaving ] = useState(false);
  const [ error, setError ] = useState('');
  const [ showHidden, setShowHidden ] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const forThisSalon = graduationDocs.filter((g) => graduationCompanyId(g, schoolId) === toggle.id);
  const visible = forThisSalon.filter((g) => !hiddenGraduationIds.includes(g.id));
  const hidden = forThisSalon.filter((g) => hiddenGraduationIds.includes(g.id));
  const byYear = (a: GraduationDoc, b: GraduationDoc) => Number(b.graduation_years ?? 0) - Number(a.graduation_years ?? 0);

  async function add() {
    setError('');
    if (!name.trim()) { setError('氏名を入力してください。'); return; }
    if (!/^\d{4}$/.test(years.trim())) { setError('卒業年度を西暦4桁の半角数字で入力してください（例：2024）。'); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, 'graduation'), {
        addedBySchoolId: schoolId,
        schoolCompanyId: toggle.id,
        school: schoolName,
        name: name.trim(),
        graduation_years: years.trim(),
        postion: position.trim(),
        time: new Date(),
      });
      setName(''); setYears(''); setPosition('');
    } catch (e) {
      console.error('卒業生を追加できませんでした', e);
      setError('卒業生を追加できませんでした。時間をおいて、もう一度お試しください。');
    } finally {
      setSaving(false);
    }
  }

  async function remove(g: GraduationDoc) {
    const bySchool = !!g.addedBySchoolId;
    const message = bySchool
      ? `${g.name}さんを削除しますか？`
      : `${g.name}さんを削除しますか？\n（この学校の画面から消えるだけで、サロンが登録したデータは残ります。あとから戻せます）`;
    if (!window.confirm(message)) return;
    try {
      if (bySchool) {
        await deleteDoc(doc(db, 'graduation', g.id));
      } else {
        await updateDoc(doc(db, 'schools', schoolId), { hiddenGraduationIds: arrayUnion(g.id) });
      }
    } catch (e) {
      console.error('卒業生を削除できませんでした', e);
      window.alert('削除できませんでした。時間をおいて、もう一度お試しください。');
    }
  }

  async function restore(g: GraduationDoc) {
    try {
      await updateDoc(doc(db, 'schools', schoolId), { hiddenGraduationIds: arrayRemove(g.id) });
    } catch (e) {
      console.error('卒業生を戻せませんでした', e);
    }
  }

  const row = (g: GraduationDoc, action: React.ReactNode) => (
    <div className={Styles.row} key={g.id}>
      <div className={Styles.colName}>
        <span>{g.name}</span>
        <span className={g.addedBySchoolId ? Styles.badgeSchool : Styles.badgeSalon}>{g.addedBySchoolId ? '学校で追加' : 'サロン登録'}</span>
      </div>
      <div className={Styles.colYear}>{g.graduation_years ? `${g.graduation_years}年` : '-'}</div>
      <div className={Styles.colPosition}>{g.postion || '-'}</div>
      <div className={Styles.colAction}>{action}</div>
    </div>
  );

  return (
    <div className={Styles.modal} role="dialog" aria-modal="true">
      <div className={Styles.background_black} onClick={() => !saving && close()}></div>
      <div className={Styles.contents}>

        <div className={Styles.modal_title}>
          <div>
            <h4>{toggle.name}の卒業生</h4>
            <p>{visible.length}人</p>
          </div>
          <RxCross2 className={Styles.modal_cross} onClick={() => !saving && close()} />
        </div>

        <div className={Styles.card}>
          <div className={Styles.head}>
            <span className={Styles.colName}>氏名</span>
            <span className={Styles.colYear}>卒業年度</span>
            <span className={Styles.colPosition}>役職</span>
            <span className={Styles.colAction} />
          </div>
          {visible.length === 0 ? (
            <p className={Styles.empty}>卒業生はまだ登録されていません。</p>
          ) : (
            [...visible].sort(byYear).map((g) => row(g, (
              <button type="button" className={Styles.textBtn} onClick={() => remove(g)}>
                削除
              </button>
            )))
          )}
        </div>

        <div className={Styles.card}>
          <p className={Styles.cardTitle}>卒業生を追加</p>
          <p className={Styles.cardDesc}>この学校の画面にだけ表示されます（サロン側には表示されません）。</p>
          <div className={Styles.form}>
            <input aria-label="氏名" placeholder="メモリー 太郎" value={name} maxLength={20} onChange={(e) => setName(e.target.value)} disabled={saving} />
            <input aria-label="卒業年度" placeholder="2010" value={years} maxLength={4} inputMode="numeric" onChange={(e) => setYears(e.target.value.replace(/[^0-9]/g, ''))} disabled={saving} />
            <input aria-label="役職（任意）" placeholder="スタイリスト" value={position} maxLength={20} onChange={(e) => setPosition(e.target.value)} disabled={saving} />
            <button type="button" className={Styles.primaryBtn} onClick={add} disabled={saving}>{saving ? '追加中...' : '追加'}</button>
          </div>
          {error && <p className={Styles.error}>{error}</p>}
        </div>

        {hidden.length > 0 && (
          <div className={Styles.card}>
            <button type="button" className={Styles.hiddenToggle} onClick={() => setShowHidden((v) => !v)}>
              削除した卒業生（{hidden.length}人）{showHidden ? 'を閉じる' : 'を見る・戻す'}
            </button>
            {showHidden && [...hidden].sort(byYear).map((g) => row(g, (
              <button type="button" className={Styles.textBtn} onClick={() => restore(g)}>戻す</button>
            )))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SchoolGraduation;
